/*
 * collection-cro.js — behaviour + analytics for the four collection-page CRO A/B tests.
 *
 * Loaded only on the four experiment collections, by snippets/collection-cro-config.liquid,
 * which has already resolved the PostHog flag and stamped the result on <html>:
 *
 *     html.cro--variant                -> this visitor is in the test arm
 *     html[data-cro-variant="..."]     -> the resolved variant key ("control" or the test key)
 *
 * This file NEVER decides the arm and NEVER swaps DOM nodes — the server renders
 * both states and CSS picks one. It only adds behaviour the variant needs
 * (client-side facets, quantity steppers, add-to-cart) and fires
 * the analytics events for BOTH arms so the experiment can actually be read.
 *
 * Plain <script defer>, no imports — matches the rest of assets/ (see free-gift.js).
 * Every handler is wrapped so a failure degrades to "no CRO behaviour", never a
 * broken collection page.
 */
(function () {
  var CFG = window.GimokaCRO;
  if (!CFG) return;

  var root = document.documentElement;
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)/;

  var state = {
    variant: 'control',
    inVariant: false,
    viewed: false
  };

  /* ------------------------------------------------------------------ *
   * Analytics
   * ------------------------------------------------------------------ */

  // Every event carries the same three properties so the four experiments can be
  // filtered apart in PostHog while sharing one event schema.
  function track(name, props) {
    try {
      if (!window.posthog || typeof window.posthog.capture !== 'function') return;
      var payload = {
        experiment: CFG.experiment,
        variant: state.variant,
        collection: CFG.collection
      };
      if (props) {
        for (var k in props) {
          if (Object.prototype.hasOwnProperty.call(props, k)) payload[k] = props[k];
        }
      }
      window.posthog.capture(name, payload);
    } catch (e) {}
  }

  function $(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }
  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  /* ------------------------------------------------------------------ *
   * Grid helpers
   * ------------------------------------------------------------------ */

  function gridItems() {
    return $$('#product-grid > .grid__item');
  }

  // 1-based position of a card in the grid, counting only cards still visible —
  // so "position" means what the shopper actually saw, not the server order.
  function positionOf(el) {
    var item = el && el.closest ? el.closest('.grid__item') : null;
    if (!item) return null;
    var visible = gridItems().filter(function (li) {
      return !li.hasAttribute('data-cro-filtered');
    });
    var idx = visible.indexOf(item);
    return idx === -1 ? null : idx + 1;
  }

  function positionOfVariantId(variantId) {
    var match = null;
    gridItems().forEach(function (li) {
      if (match) return;
      if ($('[data-variant-id="' + variantId + '"]', li)) match = li;
      if ($('input[name="id"][value="' + variantId + '"]', li)) match = li;
    });
    return match ? positionOf(match) : null;
  }

  /* ------------------------------------------------------------------ *
   * Client-side facets (variant only)
   *
   * See the header comment in snippets/collection-cro-filters.liquid for why
   * these are not native Shopify facets. Filtering hides cards with a
   * data-cro-filtered attribute; CSS does the actual hiding, and the native
   * facet form is untouched so the theme's own filters keep working alongside.
   * ------------------------------------------------------------------ */

  function activeFacets() {
    var active = { intensity: [], availability: [], collection: [] };
    $$('[data-cro-facet]').forEach(function (input) {
      if (!input.checked) return;
      var group = input.getAttribute('data-cro-facet');
      if (active[group]) active[group].push(input.value);
    });
    return active;
  }

  function matchesIntensity(item, bands) {
    if (!bands.length) return true;
    var raw = item.getAttribute('data-cro-intensity');
    if (!raw) return false; // no score (or a ranged value) -> excluded by a band filter
    var value = parseInt(raw, 10);
    if (isNaN(value)) return false;
    return bands.some(function (band) {
      var parts = band.split('-');
      return value >= parseInt(parts[0], 10) && value <= parseInt(parts[1], 10);
    });
  }

  function matchesAvailability(item, values) {
    if (!values.length) return true;
    var available = item.getAttribute('data-cro-available') === 'true';
    return values.some(function (v) {
      return v === 'in-stock' ? available : !available;
    });
  }

  function matchesCollection(item, handles) {
    if (!handles.length) return true;
    var owned = (item.getAttribute('data-cro-collections') || '').split(' ');
    return handles.some(function (h) {
      return owned.indexOf(h) !== -1;
    });
  }

  function applyFacets() {
    var active = activeFacets();
    var shown = 0;

    gridItems().forEach(function (item) {
      var ok =
        matchesIntensity(item, active.intensity) &&
        matchesAvailability(item, active.availability) &&
        matchesCollection(item, active.collection);

      if (ok) {
        item.removeAttribute('data-cro-filtered');
        shown++;
      } else {
        item.setAttribute('data-cro-filtered', '');
      }
    });

    var status = $('[data-cro-facet-status]');
    if (status) {
      status.textContent = shown + (shown === 1 ? ' product' : ' products');
    }

    var empty = $('[data-cro-facet-empty]');
    if (empty) empty.hidden = shown !== 0;
  }

  // The filter groups are rendered twice — once into the desktop sidebar
  // (small-hide) and once into the mobile filter drawer — so whichever one is
  // visible works. Mirror state across both, otherwise the hidden copy could sit
  // checked and keep filtering the grid after a resize.
  function syncFacetTwin(source) {
    var group = source.getAttribute('data-cro-facet');
    $$('[data-cro-facet="' + group + '"]').forEach(function (other) {
      if (other !== source && other.value === source.value) other.checked = source.checked;
    });
  }

  function wireFacets() {
    $$('[data-cro-facet]').forEach(function (input) {
      input.addEventListener('change', function () {
        syncFacetTwin(input);
        applyFacets();
        if (input.checked) {
          track('cro_filter_applied', {
            facet: input.getAttribute('data-cro-facet'),
            value: input.getAttribute('data-cro-facet-name') || input.value
          });
        }
      });
    });

    var reset = $('[data-cro-facet-reset]');
    if (reset) {
      reset.addEventListener('click', function () {
        $$('[data-cro-facet]').forEach(function (input) {
          input.checked = false;
        });
        applyFacets();
      });
    }

    // Native Shopify facets are tracked too, so "filter engagement" is comparable
    // between the two arms rather than only existing in the variant.
    var nativeForm = $('#FacetFiltersForm') || $('.facets__form-vertical');
    if (nativeForm) {
      nativeForm.addEventListener('change', function (event) {
        var input = event.target;
        if (!input || input.type !== 'checkbox' || !input.checked) return;
        var group = input.closest('.js-filter');
        var label = group ? group.querySelector('summary span, .facets__summary span') : null;
        track('cro_filter_applied', {
          facet: (label ? label.textContent : input.name || 'filter').trim(),
          value: (input.value || '').trim()
        });
      });
    }
  }

  /* ------------------------------------------------------------------ *
   * Quantity steppers (Professional variant)
   * ------------------------------------------------------------------ */

  function wireSteppers() {
    document.addEventListener('click', function (event) {
      var btn = event.target.closest ? event.target.closest('[data-cro-step]') : null;
      if (!btn) return;
      event.preventDefault();
      var wrap = btn.closest('[data-cro-stepper]');
      var input = wrap && $('[data-cro-qty]', wrap);
      if (!input) return;
      var next = (parseInt(input.value, 10) || 1) + parseInt(btn.getAttribute('data-cro-step'), 10);
      input.value = Math.max(1, next);
    });
  }

  /* ------------------------------------------------------------------ *
   * Add to cart (variant cards)
   *
   * The variant renders its own button rather than reusing the theme's quick-add,
   * because quick_add is set to "none" on some of these collection templates and
   * flipping that setting would change the control page too. The add itself is a
   * plain POST to routes.cart_add_url, then the cart UI is refreshed the same way
   * the rest of the theme does it.
   * ------------------------------------------------------------------ */

  // The storefront's popout cart is the SlideCart HQ app (cart_type is
  // "notification", so the theme's own drawer never renders) — same situation
  // assets/free-gift.js deals with. Ask the app to re-render, then refresh the
  // theme's cart-count bubble via the Sections Rendering API.
  function refreshCart() {
    try {
      if (typeof window.SLIDECART_UPDATE === 'function') window.SLIDECART_UPDATE();
    } catch (e) {}

    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;

    // Same shape as assets/cart-notification.js: fetch the section, then take the
    // inner HTML of its .shopify-section wrapper.
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + '?sections=cart-icon-bubble')
      .then(function (res) {
        return res.json();
      })
      .then(function (sections) {
        var html = sections['cart-icon-bubble'];
        if (!html) return;
        var next = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (next) bubble.innerHTML = next.innerHTML;
      })
      .catch(function () {});
  }

  function wireAddToCart() {
    document.addEventListener('click', function (event) {
      var btn = event.target.closest ? event.target.closest('[data-cro-add]') : null;
      if (!btn || btn.hasAttribute('aria-busy')) return;
      event.preventDefault();

      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;

      var stepper = btn.closest('.cro-card__buyrow');
      var qtyInput = stepper && $('[data-cro-qty]', stepper);
      var quantity = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;

      btn.setAttribute('aria-busy', 'true');

      fetch(CFG.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity }] })
      })
        .then(function (res) {
          return res.json();
        })
        .then(function () {
          refreshCart();
          try {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'collection-cro' });
            }
          } catch (e) {}
        })
        .catch(function () {})
        .then(function () {
          btn.removeAttribute('aria-busy');
        });
    });

    // "Choose pack size" — the secondary route to the PDP for the 100/200 tiers.
    document.addEventListener('click', function (event) {
      var link = event.target.closest ? event.target.closest('[data-cro-pack-size]') : null;
      if (!link) return;
      track('cro_pack_size_clicked', { product: link.getAttribute('data-product-handle') });
    });
  }

  /* ------------------------------------------------------------------ *
   * Cross-sell / wholesale / FAQ tracking (fires in BOTH arms where the
   * element exists, so the secondary metrics are comparable)
   * ------------------------------------------------------------------ */

  function wireLinkTracking() {
    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target.closest) return;

      var crossSell = target.closest('[data-cro-cross-sell]');
      if (crossSell) {
        track('cro_cross_sell_clicked', {
          target_collection: crossSell.getAttribute('data-cro-cross-sell')
        });
      }

      var wholesale = target.closest('[data-cro-wholesale]');
      if (wholesale) {
        track('cro_wholesale_clicked', { cta: wholesale.getAttribute('data-cro-wholesale') });
      }
    });
  }

  function wireFaqTracking() {
    // The theme renders FAQs as <details> (snippets/collection-faqs.liquid), which
    // exists in both arms — the variant only changes its layout.
    $$('.brand-faqs details, .cro-faqs details').forEach(function (details) {
      details.addEventListener('toggle', function () {
        if (!details.open) return;
        var summary = details.querySelector('.accordion__title, summary');
        track('cro_faq_opened', {
          question: summary ? summary.textContent.trim().slice(0, 200) : ''
        });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Cart watching
   *
   * The theme's pubsub only fires for cart changes the THEME makes. The storefront
   * cart is the SlideCart HQ app, and app-side add/remove never touches pubsub —
   * the same problem assets/free-gift.js solves. So subscribe to pubsub AND watch
   * fetch/XHR for /cart/* mutations. Our own requests are tagged so they don't loop.
   * ------------------------------------------------------------------ */

  function trackAddFromResponse(payload) {
    if (!payload) return;
    var items = payload.items || (payload.id ? [payload] : []);
    items.forEach(function (item) {
      track('cro_card_add_to_cart', {
        product: item.handle || item.product_handle || item.product_title,
        price: Number(((item.price || 0) / 100).toFixed(2)),
        position: positionOfVariantId(item.variant_id || item.id)
      });
    });
  }

  function watchCart() {
    try {
      // Nothing subscribes to the theme's cartUpdate any more. The fetch/XHR
      // interception below stays: it is what fires cro_card_add_to_cart in BOTH
      // arms, including control's own quick-add, which is the primary metric.
    } catch (e) {}

    // fetch
    try {
      var nativeFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : input && input.url;
        var isCart = url && CART_MUTATION_RE.test(url);
        var isAdd = isCart && /\/cart\/add/.test(url);
        var call = nativeFetch.apply(this, arguments);
        if (!isCart) return call;
        return call.then(function (response) {
          if (isAdd && response.ok) {
            // Clone so the caller still gets an unread body.
            response
              .clone()
              .json()
              .then(trackAddFromResponse)
              .catch(function () {});
          }
          return response;
        });
      };
    } catch (e) {}

    // XHR (Appstle and some apps use it directly)
    try {
      var open = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        if (url && CART_MUTATION_RE.test(url)) {
          this.addEventListener('load', function () {});
        }
        return open.apply(this, arguments);
      };
    } catch (e) {}
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  // Called on first resolution and again whenever the arm changes — which happens
  // when someone runs posthog.featureFlags.overrideFeatureFlags() in the console.
  function onResolved(variant) {
    state.variant = variant;
    state.inVariant = variant !== 'control';

    if (!state.viewed) {
      state.viewed = true;
      // Fired for BOTH arms, once per pageview. PostHog records the exposure
      // itself via getFeatureFlag ($feature_flag_called); this is the page-level
      // companion event, so control and variant pageviews are comparable.
      track('cro_variant_viewed');
    }

    if (state.inVariant) {
      applyFacets();
    }
  }

  function boot() {
    try {
      wireFacets();
      wireSteppers();
      wireAddToCart();
      wireLinkTracking();
      wireFaqTracking();
      watchCart();

      // Stay subscribed rather than listening once: the arm can change after load
      // when a QA override is applied from the console.
      document.addEventListener('gimoka:cro:resolved', function (event) {
        onResolved(event.detail.variant);
      });

      // The config snippet runs before this deferred file, so it has usually
      // resolved already and the event has been and gone — pick it up from <html>.
      var resolved = root.getAttribute('data-cro-variant');
      if (resolved) onResolved(resolved);
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();